import { Controller } from '@hotwired/stimulus';
import { dispatchEvent } from 'stimulus-library';

export default class extends Controller {
  static targets = ['target', 'template'];

  static values = {
    insertMode: String,
    wrapperClass: String,
  };

  get wrapperClass() {
    return this.hasWrapperSelectorValue ? this.wrapperClassValue : 'nested-fields';
  }

  get insertMode() {
    return this.hasInsertModeValue ? this.insertModeValue : 'beforeend';
  }

  add(event) {
    if (event) {
      event.preventDefault();
    }
    const { templateSelector } = event.params;

    const content = this.templateTarget.innerHTML;
    let fragment = document.createRange().createContextualFragment(content);
    const id = this.generateID();

    // Nested form inputs need to have consistent IDs
    // In order to update a Posts name, you expect an input to have
    // post[123][title]
    // When you have nested fields, the ID needs to be consistent for all subfields
    // i.e. post[123][comments_attributes][0][title]
    // This little feat of engineering will replace only the FIRST instance of NEW_RECORD in each input's name attribute with the
    // current generated ID, leaving other instances of NEW_RECORD alone for the next nested field controller.
    // i.e. post[THIS_ID][comments_attributes][NEW_RECORD][title]
    // Currently only works for 2 levels of nesting. If you need to go deeper, the templateSelector will need to be recursive.
    const inputs = fragment.querySelectorAll('[name]');
    this.replaceFirstLevelNewRecordIDs(inputs, id);
    let templates = Array.from(fragment.querySelectorAll('template'));
    templates = templates.map((template) => template.content.querySelectorAll('[name]'));
    templates.forEach((template) => this.replaceFirstLevelNewRecordIDs(template, id));

    if (templateSelector) {
      fragment = fragment.querySelector(templateSelector);
    } else {
      fragment = fragment.firstElementChild;
    }
    if (fragment == null) {
      throw new Error('Could not find a template element');
    }

    const insertedEl = this.targetTarget.insertAdjacentElement(this.insertMode, fragment);
    dispatchEvent(this, insertedEl, 'nested-form:added', { detail: { fragment, id } });
  }

  remove(event) {
    event.preventDefault();
    const wrapper = event.target.closest(`.${this.wrapperClass}`);
    if (wrapper == null) {
      throw new Error(`#remove was clicked from outside of a child record. Could not find an ancestor with class .${this.wrapperClass}`);
    }

    if (wrapper.dataset.newRecord === 'true') {
      wrapper.remove();
    } else {
      wrapper.style.display = 'none';
      const destroyInput = wrapper.querySelector('input[name*=\'_destroy\']');
      if (destroyInput == null) {
        throw new Error('Could not find a hidden input with name \'_destroy\'. NestedForm cannot remove an already persisted record without it.');
      }

      destroyInput.value = '1';
      const changeEvent = new CustomEvent('change', { bubbles: true, cancelable: true, detail: { target: destroyInput } });
      destroyInput.dispatchEvent(changeEvent);
    }
  }

  generateID() {
    return new Date().getTime().toString() + Math.random().toString().slice(2);
  }

  replaceFirstLevelNewRecordIDs(nodes, id) {
    nodes.forEach((node) => {
      const name = node.getAttribute('name').replace(/NEW_RECORD/, id);
      node.setAttribute('name', name);
    });
  }
}
